import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ADD_PROJECT_TEAM_MEMBER } from '../../graphql/mutations';
import { GET_ACCOUNT_DETAILS, GET_PROJECT } from '../../graphql/queries';
import { toast } from 'sonner';
import { Loader } from '../common/PageLoader';

interface AddTeamMemberModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  accountId: string;
  existingTeamMemberIds: string[]; // Array of user IDs already in the project
}

export function AddTeamMemberModal({
  open,
  onClose,
  projectId,
  accountId,
  existingTeamMemberIds,
}: AddTeamMemberModalProps) {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('Team Member');

  // Get account details to fetch team members
  const { data: accountData, loading: accountLoading } = useQuery(GET_ACCOUNT_DETAILS, {
    variables: { id: accountId },
    skip: !accountId,
  });

  // Mutation to add team member
  const [addProjectTeamMember, { loading: addingMember }] = useMutation(ADD_PROJECT_TEAM_MEMBER, {
    refetchQueries: [
      { query: GET_PROJECT, variables: { id: projectId } },
      { query: GET_ACCOUNT_DETAILS, variables: { id: accountId } },
    ],
    onCompleted: () => {
      toast.success('Team member added successfully!');
      handleClose();
    },
    onError: (error) => {
      toast.error(`Failed to add team member: ${error.message}`);
    },
  });

  const handleClose = () => {
    setSelectedUserId('');
    setSelectedRole('Team Member');
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedUserId) {
      toast.error('Please select a team member');
      return;
    }

    addProjectTeamMember({
      variables: {
        input: {
          projectId,
          userId: selectedUserId,
          role: selectedRole,
        },
      },
    });
  };

  // Get available team members (account users not already in project)
  const accountUsers = accountData?.account?.users || [];
  const availableUsers = accountUsers.filter(
    (userAccount: any) => !existingTeamMemberIds.includes(userAccount.user.id)
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl bg-core-dark text-white">
        <DialogHeader>
          <DialogTitle>Add Team Member</DialogTitle>
          <DialogDescription>
            Select a team member from your account to add to this project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {accountLoading ? (
            <Loader label="Loading team members" className="text-cmp-ink-2" />
          ) : availableUsers.length === 0 ? (
            <p className="text-cmp-ink-2">
              All account team members are already added to this project.
            </p>
          ) : (
            <>
              {/* Team Member Selection */}
              <div className="space-y-2">
                <Label htmlFor="teamMember">Team Member *</Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers.map((userAccount: any) => (
                      <SelectItem key={userAccount.user.id} value={userAccount.user.id}>
                        {userAccount.user.email} {userAccount.role && `(${userAccount.role})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Role Selection */}
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Team Member">Team Member</SelectItem>
                    <SelectItem value="Editor">Editor</SelectItem>
                    <SelectItem value="Reviewer">Reviewer</SelectItem>
                    <SelectItem value="Viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedUserId || addingMember || availableUsers.length === 0}
          >
            {addingMember ? 'Inviting...' : 'New Member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
